import React from 'react'
import {
  FileText,
  FileSearch,
  Brain,
  Layers3,
  CheckCircle2,
  Check,
  type LucideIcon
} from 'lucide-react'
import AIAgentOrbLoader from '../../../Dashboard/AIAgentOrb/AIAgentOrbLoader.tsx'
import orbStyles from '../../../Dashboard/AIAgentOrb/AIAgentOrbLoader.module.scss'
import type { DocumentationPipelineStageProgress } from '../../../../services/projectAutofillDraftService.ts'
import { formatDocumentationPipelineFileSize } from '../../../../services/projectAutofillDraftService.ts'
import styles from './MaterialsQuotationPipelineLoader.module.scss'



const MATERIALS_PIPELINE_STEPS: { phase: string; label: string; Icon: LucideIcon }[] = [
  { phase: 'reading_file', label: 'Leyendo el archivo', Icon: FileText },
  { phase: 'extracting_text', label: 'Extrayendo texto', Icon: FileSearch },
  { phase: 'llm_inference', label: 'Interpretación con IA', Icon: Brain },
  { phase: 'catalog_matching', label: 'Catálogo Edify', Icon: Layers3 },
  { phase: 'finalizing', label: 'Finalizando borrador', Icon: CheckCircle2 }
]



const CANONICAL_STAGE: Record<string, string> = {
  draft_ready: 'reading_file',
  analyzing_document: 'llm_inference',
  matching_skills: 'catalog_matching',
  suggesting_budget_sheets: 'catalog_matching',
  saving_draft: 'finalizing'
}



const TOP_LEVEL_PHASE_LABELS: Record<string, string> = {
  no_document: 'Esperando documento…',
  awaiting_analysis: 'Preparando el análisis del listado…',
  processing: 'Procesando el archivo…',
  ready: 'Análisis completado',
  failed: 'El análisis no pudo completarse'
}



function canonicalPipelinePhase(phase: string | null): string | null {
  if (!phase) return null
  return CANONICAL_STAGE[phase] ?? phase
}



const phaseIndex = (phase: string | null): number => {
  const c = canonicalPipelinePhase(phase)
  if (!c) return -1
  const i = MATERIALS_PIPELINE_STEPS.findIndex((s) => s.phase === c)
  return i >= 0 ? i : -1
}



const labelForPipelinePhase = (phase: string): string => {
  const fromTop = TOP_LEVEL_PHASE_LABELS[phase]
  if (fromTop) return fromTop
  const c = canonicalPipelinePhase(phase)
  const step = c ? MATERIALS_PIPELINE_STEPS.find((s) => s.phase === c) : undefined
  if (step) return step.label
  return phase
    .split(/_/g)
    .map((w) => (w.length ? w.charAt(0).toLocaleUpperCase('es') + w.slice(1).toLowerCase() : ''))
    .filter(Boolean)
    .join(' ')
}



type MaterialsQuotationPipelineLoaderProps = {
  pipelinePhase?: string | null
  stageProgress?: DocumentationPipelineStageProgress | null
  primaryFileOriginalName?: string | null
  primaryFileSizeBytes?: number | null
}



const MaterialsQuotationPipelineLoader: React.FC<MaterialsQuotationPipelineLoaderProps> = ({
  pipelinePhase = null,
  stageProgress = null,
  primaryFileOriginalName = null,
  primaryFileSizeBytes = null
}) => {
  const canonical = canonicalPipelinePhase(pipelinePhase)
  const activeIdx = phaseIndex(pipelinePhase)
  const unknown = Boolean(pipelinePhase && activeIdx < 0 && !TOP_LEVEL_PHASE_LABELS[pipelinePhase ?? ''])
  const unknownLabel = unknown && pipelinePhase ? labelForPipelinePhase(pipelinePhase) : ''
  const totalSteps = MATERIALS_PIPELINE_STEPS.length
  const stepNum = activeIdx >= 0 ? activeIdx + 1 : null
  const primaryName =
    typeof primaryFileOriginalName === 'string' && primaryFileOriginalName.trim().length > 0
      ? primaryFileOriginalName.trim()
      : null

  const sizeLabel =
    typeof primaryFileSizeBytes === 'number' &&
    primaryFileSizeBytes > 0 &&
    Number.isFinite(primaryFileSizeBytes)
      ? formatDocumentationPipelineFileSize(primaryFileSizeBytes)
      : null

  const indeterminateBar =
    canonical === 'reading_file' ||
    canonical === 'extracting_text' ||
    pipelinePhase === 'processing'

  const hasCatalogProgress =
    canonical === 'catalog_matching' &&
    stageProgress != null &&
    stageProgress.total > 0 &&
    stageProgress.done >= 0

  const catalogFraction =
    hasCatalogProgress && stageProgress ? Math.min(1, stageProgress.done / stageProgress.total) : 0

  const progressPct =
    activeIdx >= 0
      ? hasCatalogProgress
        ? Math.round(((activeIdx + catalogFraction) / totalSteps) * 100)
        : Math.round(((activeIdx + 1) / totalSteps) * 100)
      : unknown
        ? 12
        : indeterminateBar
          ? 14 : 6

  const stepTitle =
    activeIdx >= 0 ? MATERIALS_PIPELINE_STEPS[activeIdx].label : 'Enviando archivo…'

  const phaseNamedTitle =
    canonical === 'reading_file'
      ? primaryName
        ? `Leyendo ${primaryName}…`
        : 'Leyendo el archivo…'
      : canonical === 'extracting_text' && primaryName
        ? `Extrayendo texto de ${primaryName}…`
        : canonical === 'llm_inference' && primaryName
          ? `Interpretando ${primaryName} con IA…`
          : null

  const catalogSubtitle =
    hasCatalogProgress && stageProgress
      ? `Relacionando materiales con el catálogo (${stageProgress.done} de ${stageProgress.total}).`
      : null

  const waitingTopLevelOnly =
    pipelinePhase != null &&
    Boolean(TOP_LEVEL_PHASE_LABELS[pipelinePhase]) &&
    activeIdx < 0 &&
    !unknown

  const defaultTitle = unknown
    ? unknownLabel
    : activeIdx >= 0
      ? phaseNamedTitle ?? MATERIALS_PIPELINE_STEPS[activeIdx].label
      : waitingTopLevelOnly && pipelinePhase
        ? TOP_LEVEL_PHASE_LABELS[pipelinePhase]
        : 'Enviando archivo…'

  const mainTitle = defaultTitle

  const eyebrowText =
    primaryName != null
      ? primaryFileSizeBytes != null && primaryFileSizeBytes > 0 && sizeLabel
        ? `Importando listado · ${sizeLabel}`
        : 'Importación de listado de materiales'
      : 'Importación de listado de materiales'

  const showLargeFileHint =
    typeof primaryFileSizeBytes === 'number' && primaryFileSizeBytes >= 3 * 1024 * 1024

  const liveStatus = unknown
    ? `Estado: ${unknownLabel}`
    : activeIdx >= 0
      ? `${stepTitle}${primaryName ? ` (${primaryName})` : ''}.` +
        (catalogSubtitle ? ` ${catalogSubtitle}` : ` Paso ${stepNum} de ${totalSteps}.`)
      : waitingTopLevelOnly && pipelinePhase
        ? TOP_LEVEL_PHASE_LABELS[pipelinePhase]
        : 'Enviando archivo al servidor.'

  const waitingForFirstPhase = activeIdx < 0 && !unknown



  return (
    <div className={styles.wrap} aria-busy="true" data-edify-materials-loader="quotation-pipeline">
      <div className={styles.shimmerRail} aria-hidden>
        <div className={styles.shimmer} />
      </div>
      <div className={styles.progressBarTrack} aria-hidden>
        <div className={styles.progressBarFill} style={{ width: `${progressPct}%` }} />
      </div>
      <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
        {liveStatus}
      </div>
      <div className={styles.header}>
        <AIAgentOrbLoader status="thinking" className={`${orbStyles.thinkingOrb} ${styles.orb}`} />
        <div className={styles.headerText}>
          <span className={styles.eyebrow}>{eyebrowText}</span>
          <span className={styles.title}>{mainTitle}</span>
          {canonical === 'extracting_text' ? (
            <span className={styles.progressMetaMuted}>
              {sizeLabel ? `Tamaño aproximado: ${sizeLabel}. ` : null}
              Los archivos grandes pueden tardar varios minutos en esta etapa.
            </span>
          ) : catalogSubtitle ? (
            <span className={styles.progressMeta}>{catalogSubtitle}</span>
          ) : waitingTopLevelOnly ? (
            <span className={styles.progressMetaMuted}>
              En cuanto el servidor asigne el trabajo vas a ver el avance paso a paso.
            </span>
          ) : stepNum != null ? (
            <span className={styles.progressMeta}>
              Paso {stepNum} de {totalSteps}
              <span className={styles.progressMetaHint}>
                {showLargeFileHint
                  ? 'Los archivos grandes pueden tardar varios minutos al leerse.'
                  : 'El reconocimiento del listado puede tardar varios minutos.'}
              </span>
            </span>
          ) : (
            <span className={styles.progressMetaMuted}>Esperando la primera respuesta del servidor…</span>
          )}
        </div>
      </div>
      <ol className={styles.steps} aria-label="Etapas de importación del listado">
        {MATERIALS_PIPELINE_STEPS.map(({ phase, label, Icon }, i) => {
          const active = activeIdx >= 0 && i === activeIdx
          const done = activeIdx >= 0 && i < activeIdx
          const waitingSlot = waitingForFirstPhase && i === 0
          const stepClass = `${styles.step} ${active ? styles.stepActive : ''} ${done ? styles.stepDone : ''} ${waitingSlot ? styles.stepWaitingServer : ''}`
          return (
            <li key={phase} className={stepClass} aria-current={active ? 'step' : undefined}>
              <span className={styles.stepIcon} aria-hidden>
                {done ? <Check size={16} strokeWidth={2.25} /> : <Icon size={16} strokeWidth={1.85} />}
              </span>
              <span className={styles.stepLabel}>{label}</span>
              {active ? <span className={styles.stepNowBadge}>En curso</span> : null}
              {waitingSlot ? <span className={styles.stepWaitingBadge}>Esperando…</span> : null}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default MaterialsQuotationPipelineLoader

